import { type FC, type PropsWithChildren, useState } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type UseFormSetValue,
} from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { BACKGROUND_FIELD_DESCRIPTIONS } from '#apps/client-web/features/audit-form-background/constants';
import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { useFormDescriptions } from '#apps/client-web/features/settings/hooks/use-form-descriptions';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';
import type { OperationScheduleEntry } from '#packages/data-collection/models/audit-background-form/categories/general';

const DAYS = [
  { weekDay: 0, letter: 'S', name: 'Sunday' },
  { weekDay: 1, letter: 'M', name: 'Monday' },
  { weekDay: 2, letter: 'T', name: 'Tuesday' },
  { weekDay: 3, letter: 'W', name: 'Wednesday' },
  { weekDay: 4, letter: 'T', name: 'Thursday' },
  { weekDay: 5, letter: 'F', name: 'Friday' },
  { weekDay: 6, letter: 'S', name: 'Saturday' },
];

type ScheduleEntry = OperationScheduleEntry;

type CopyState = {
  anchor: HTMLElement;
  weekDay: number;
  targets: Set<number>;
};

export type OperationHoursFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
  setValue: UseFormSetValue<EditAuditFormBackgroundData>;
};

export const OperationHoursFormDialog: FC<
  PropsWithChildren<OperationHoursFormDialogProps>
> = ({ open, control, setValue, onClose }) => {
  const getDescription = useFormDescriptions(BACKGROUND_FIELD_DESCRIPTIONS);

  const schedule = useWatch({ control, name: 'general.operationSchedule' });
  const [copyState, setCopyState] = useState<CopyState | null>(null);

  const getDay = (weekDay: number) =>
    schedule?.find((entry) => entry.weekDay === weekDay);

  const updateDay = (weekDay: number, patch: Partial<ScheduleEntry>) => {
    const current = schedule ?? [];
    const index = current.findIndex((entry) => entry.weekDay === weekDay);
    if (index >= 0) {
      setValue(
        'general.operationSchedule',
        current.map((entry, i) =>
          i === index ? { ...entry, ...patch } : entry,
        ),
      );
    } else {
      setValue('general.operationSchedule', [
        ...current,
        { weekDay, ...patch },
      ]);
    }
  };

  const handleApplyCopy = () => {
    if (!copyState) return;
    const source = getDay(copyState.weekDay);
    let updated = [...(schedule ?? [])];
    copyState.targets.forEach((targetWeekDay) => {
      const entry: ScheduleEntry = {
        weekDay: targetWeekDay,
        startTime: source?.startTime,
        endTime: source?.endTime,
        isUnavailable: source?.isUnavailable ?? false,
      };
      const index = updated.findIndex((e) => e.weekDay === targetWeekDay);
      updated =
        index >= 0
          ? updated.map((e, i) => (i === index ? entry : e))
          : [...updated, entry];
    });
    setValue('general.operationSchedule', updated);
    setCopyState(null);
  };

  return (
    <SubFormDialog title="Operation Hours" open={open} onClose={onClose}>
      <Stack spacing={3}>
        <Stack spacing={1.5}>
          {DAYS.map(({ weekDay, letter, name }) => {
            const day = getDay(weekDay);
            const isUnavailable = day?.isUnavailable ?? false;

            return (
              <Stack
                key={weekDay}
                direction="row"
                alignItems="center"
                spacing={1}>
                <Avatar
                  sx={{
                    bgcolor: isUnavailable ? 'action.disabled' : 'primary.main',
                    width: 32,
                    height: 32,
                    fontSize: '0.75rem',
                    flexShrink: 0,
                  }}>
                  {letter}
                </Avatar>
                {isUnavailable ? (
                  <>
                    <Typography flex={1} variant="body2" color="textSecondary">
                      Unavailable
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={`Add ${name}`}
                      onClick={() =>
                        updateDay(weekDay, { isUnavailable: false })
                      }>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <TextField
                      type="time"
                      size="small"
                      value={day?.startTime ?? ''}
                      onChange={(e) =>
                        updateDay(weekDay, { startTime: e.target.value })
                      }
                      sx={{ flex: 1 }}
                      slotProps={{
                        htmlInput: { 'aria-label': `${name} start time` },
                      }}
                    />
                    <Typography variant="body2" color="textSecondary">
                      —
                    </Typography>
                    <TextField
                      type="time"
                      size="small"
                      value={day?.endTime ?? ''}
                      onChange={(e) =>
                        updateDay(weekDay, { endTime: e.target.value })
                      }
                      sx={{ flex: 1 }}
                      slotProps={{
                        htmlInput: { 'aria-label': `${name} end time` },
                      }}
                    />
                    <IconButton
                      size="small"
                      aria-label={`Mark ${name} unavailable`}
                      onClick={() =>
                        updateDay(weekDay, { isUnavailable: true })
                      }>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Copy ${name} times`}
                      onClick={(e) =>
                        setCopyState({
                          anchor: e.currentTarget,
                          weekDay,
                          targets: new Set(),
                        })
                      }>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Stack>
            );
          })}
        </Stack>

        <Controller
          control={control}
          name="general.operationHoursDescription"
          rules={{ maxLength: 120 }}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              {...getFormErrorAttributes(fieldState.error)}>
              <TextField
                {...field}
                label="Operation hours description"
                type="text"
                variant="outlined"
                multiline
                rows={3}
                {...getFormErrorAttributes(fieldState.error)}
              />
              <FormHelperText>{fieldState.error?.message ?? getDescription(field.name)}</FormHelperText>
            </FormControl>
          )}
        />
      </Stack>

      <Popover
        open={!!copyState}
        anchorEl={copyState?.anchor}
        onClose={() => setCopyState(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography
            variant="overline"
            color="textSecondary"
            display="block"
            sx={{ mb: 0.5 }}>
            Copy times to...
          </Typography>
          <Stack>
            {DAYS.filter((d) => d.weekDay !== copyState?.weekDay).map(
              ({ weekDay, name }) => (
                <FormControlLabel
                  key={weekDay}
                  label={name}
                  sx={{ justifyContent: 'space-between', ml: 0, mr: 0 }}
                  labelPlacement="start"
                  control={
                    <Checkbox
                      size="small"
                      checked={copyState?.targets.has(weekDay) ?? false}
                      onChange={(e) => {
                        if (!copyState) return;
                        const next = new Set(copyState.targets);
                        e.target.checked
                          ? next.add(weekDay)
                          : next.delete(weekDay);
                        setCopyState({ ...copyState, targets: next });
                      }}
                    />
                  }
                />
              ),
            )}
          </Stack>
          <Button
            fullWidth
            variant="contained"
            size="small"
            sx={{ mt: 1 }}
            onClick={handleApplyCopy}>
            Apply
          </Button>
        </Box>
      </Popover>
    </SubFormDialog>
  );
};
